export interface CategoryFaq {
  q: string;
  a: string;
}

export interface CategoryInfo {
  slug: string;
  description: string;
  faqs: CategoryFaq[];
}

export const categoryInfo: Record<string, CategoryInfo> = {
  'boat-builders-repair': {
    slug: 'boat-builders-repair',
    description: 'New Zealand has a world-class boat building and repair industry, from custom alloy trailer boats to superyacht refits. Find builders, repairers and refit specialists across the country.',
    faqs: [
      {
        q: 'How much does it cost to build a boat in New Zealand?',
        a: 'A small custom alloy trailer boat typically starts around $30,000-$60,000, while production fibreglass boats range from $80,000 to several hundred thousand. Custom launches and yachts commonly exceed $500,000. Always get an itemised written quote and agree on a payment schedule tied to build milestones.',
      },
      {
        q: 'Do I need insurance approval before getting boat repairs done?',
        a: 'Yes, if you intend to claim the repair on insurance. Contact your insurer before any work starts; they will usually appoint an assessor to inspect the damage and approve a repairer and quote. Starting work without approval can void your claim.',
      },
      {
        q: 'What is the difference between GRP, alloy and timber boat construction?',
        a: 'GRP (fibreglass) is low-maintenance and common in production boats but heavier. Alloy is light, tough and popular for NZ trailer boats but can suffer electrolysis if wiring is poor. Timber is beautiful and repairable but demands the most ongoing maintenance.',
      },
      {
        q: 'How often should a boat be surveyed?',
        a: 'Always get a pre-purchase survey when buying second-hand. For insurance purposes, many insurers require a survey every 5-10 years once a vessel passes 20-25 years of age, or after significant modifications or damage.',
      },
      {
        q: 'Can I claim storm or accidental damage on my boat insurance?',
        a: 'Comprehensive policies generally cover storm, collision and accidental damage, including salvage costs. Document the damage with photos immediately, prevent further damage where safe, and notify your insurer as soon as possible.',
      },
    ],
  },
  'boat-charters': {
    slug: 'boat-charters',
    description: 'From Auckland harbour party boats to Bay of Islands sailing charters, New Zealand charter operators cater for corporate functions, birthdays, weddings and day trips on the water.',
    faqs: [
      {
        q: 'Do charter boats in New Zealand come with a skipper?',
        a: 'Almost all NZ charter vessels are skippered by a qualified master, which is required for vessels carrying paying passengers under Maritime NZ rules. Bareboat (self-drive) charters are rare and usually only available to experienced skippers with references.',
      },
      {
        q: 'How much does a party boat cost in Auckland?',
        a: 'Expect to pay from around $1,500-$5,000+ for a private function, depending on vessel size, trip duration, guest numbers and catering. Per-person public cruises and fishing trips are cheaper options for small groups.',
      },
      {
        q: 'What should I bring on a harbour cruise?',
        a: 'Bring warm layers (it is always cooler on the water), sunscreen, sunglasses and soft-soled non-marking shoes. Most operators provide food and drink or allow BYO by arrangement. Check with your operator about their liquor licence conditions.',
      },
      {
        q: 'Can charter operators cater for dietary requirements?',
        a: 'Yes, most operators cater for vegetarian, vegan, gluten-free and allergy requirements with advance notice. Confirm details at least a week before your trip, especially for larger groups.',
      },
      {
        q: 'How far in advance should I book a charter?',
        a: 'For summer weekends (December to March), book 4-8 weeks ahead, and earlier for Christmas corporate functions. Shoulder season and midweek trips can often be arranged at shorter notice.',
      },
    ],
  },
  'boat-ramps-launches': {
    slug: 'boat-ramps-launches',
    description: 'New Zealand has hundreds of public boat ramps, from all-tide concrete ramps to beach launches. MarineLink lists 250+ ramps with GPS locations and Google Maps links to help you plan your launch.',
    faqs: [
      {
        q: 'Do you have to pay to use a boat ramp in NZ?',
        a: 'Many council ramps are free, but some councils charge a casual fee (typically $10-$20) or require an annual permit. Auckland, Tauranga and some other councils use paid permits at busy ramps. Check signage at the ramp or the local council website.',
      },
      {
        q: 'What is proper boat ramp etiquette?',
        a: 'Prepare your boat in the rigging area, not on the ramp. Launch or retrieve quickly, then move clear so others can use the lane. Never block the ramp while parking, loading gear or waiting for crew. Offer to help solo boaters where you can.',
      },
      {
        q: 'Does my boat trailer need a WOF?',
        a: 'Yes. Boat trailers used on public roads require a current Warrant of Fitness and registration, just like any light trailer. Brakes are required on trailers with a laden weight over 2,000kg (or 2,500kg for some classes).',
      },
      {
        q: 'What is the best tide for launching at a boat ramp?',
        a: 'It depends on the ramp. Deep all-tide ramps work at any tide, while shallow or short ramps are best used within two hours either side of high tide. MarineLink listings note tidal restrictions where known, and local knowledge is invaluable.',
      },
      {
        q: 'Can I leave my trailer at the boat ramp overnight?',
        a: 'Rules vary by council. Some ramp car parks prohibit overnight parking or have time limits, while others allow it. Check the signage at the ramp; fines and towing apply at some busy locations.',
      },
    ],
  },
  'boat-rentals': {
    slug: 'boat-rentals',
    description: 'Boat rental operators across New Zealand offer everything from dinghies and kayaks to powered runabouts and houseboats, no ownership required.',
    faqs: [
      {
        q: 'Do I need a licence to rent a boat in New Zealand?',
        a: 'There is no legal licence requirement for recreational powerboats in NZ, but rental operators set their own rules. Most require a briefing, some require prior experience or a boating course certificate, and jetski rentals usually have stricter conditions.',
      },
      {
        q: 'How old do you have to be to hire a boat?',
        a: 'Most operators require the hirer to be at least 18, and some set the minimum at 21 or 25 for larger or faster vessels. A responsible adult must usually accompany younger drivers.',
      },
      {
        q: 'Is insurance included in boat rental?',
        a: 'Operators carry their own insurance, but you will usually pay a bond and be liable for an excess if the boat is damaged. Ask about the excess amount and consider whether your travel or contents insurance covers hired watercraft.',
      },
      {
        q: 'What safety gear comes with a rental boat?',
        a: 'Rental boats must carry legally required safety equipment, including correctly sized lifejackets for everyone on board. Most operators also supply an anchor, bailer, paddle and basic navigation information for the local area.',
      },
      {
        q: 'Can I take a rental boat anywhere I like?',
        a: 'No. Operators set operating limits, such as staying within a harbour, inside a bar, or within a set distance from base. Exceeding these limits can void the hire agreement and their insurance.',
      },
    ],
  },
  'boat-transport': {
    slug: 'boat-transport',
    description: 'Professional boat transporters move vessels by road throughout New Zealand, from trailer boats to overdimension launches requiring pilot vehicles and permits.',
    faqs: [
      {
        q: 'How much does boat transport cost in New Zealand?',
        a: 'Costs depend on distance, vessel size and overdimension requirements. Moving a trailer boat from Auckland to Tauranga typically costs $800-$1,500, while large or overdimension vessels needing permits and pilot vehicles cost significantly more. Get written quotes from several carriers.',
      },
      {
        q: 'How should I prepare my boat for road transport?',
        a: 'Remove or secure all loose gear, electronics and personal items. Drain water and fuel tanks where practical, secure hatches and covers, tape down anything that can lift, and raise or remove aerials and outriggers. Your transporter will advise on specific requirements.',
      },
      {
        q: 'Is my boat insured while being transported?',
        a: 'Do not assume so. Ask the carrier for their goods-in-transit liability cover and limits, and check your own boat insurance policy; many cover road transport, sometimes requiring notification. Get the cover details in writing before the move.',
      },
      {
        q: 'Can oversized boats be moved by road in NZ?',
        a: 'Yes. Loads over 2.5m wide or 4.3m high require overdimension permits, and larger loads need pilot vehicles and travel time restrictions. Specialist boat transporters handle the permitting as part of the job.',
      },
      {
        q: 'Do boat transporters launch and retrieve the boat as well?',
        a: 'Many offer door-to-ramp or ramp-to-ramp service and will launch or retrieve the vessel at each end for an additional fee. Confirm exactly what is included when comparing quotes.',
      },
    ],
  },
  'boats-for-sale': {
    slug: 'boats-for-sale',
    description: 'Buying or selling a boat in New Zealand? Browse the MarineLink marketplace and learn what to check before you buy, from surveys to PPSR searches.',
    faqs: [
      {
        q: 'What should I check before buying a used boat?',
        a: 'Check hull condition (osmosis in GRP, corrosion in alloy), engine hours and service history, trailer condition and WOF, and that all safety gear is included. Always arrange a professional marine survey for anything beyond a small dinghy, and sea-trial the boat before committing.',
      },
      {
        q: 'Do I need a marine survey when buying a boat?',
        a: 'Strongly recommended, especially for vessels over $20,000. A surveyor identifies structural, mechanical and safety issues that are invisible to most buyers, and the report gives you negotiating power. Insurers may also require a survey on older vessels.',
      },
      {
        q: 'How do I check if there is money owing on a boat?',
        a: 'Do a PPSR (Personal Property Securities Register) search on the hull identification number and the seller. A $2 search shows registered security interests, protecting you from buying a boat that a finance company can repossess.',
      },
      {
        q: 'What are the ongoing costs of owning a boat?',
        a: 'A common rule of thumb is 10% of the boat\'s value per year covering berthage or storage, insurance, maintenance, antifouling and fuel. Marina berths alone run $3,000-$15,000+ per year depending on size and location.',
      },
      {
        q: 'Can I get finance to buy a boat in NZ?',
        a: 'Yes. Several NZ lenders offer specialist marine finance, usually secured against the vessel with terms of 1-7 years. Rates and deposit requirements vary, so compare offers and check early repayment terms.',
      },
    ],
  },
  'diving-water-sports': {
    slug: 'diving-water-sports',
    description: 'New Zealand offers world-class diving, from the Poor Knights Islands to Fiordland, plus wakeboarding, paddleboarding, kayaking and other water sports operators nationwide.',
    faqs: [
      {
        q: 'Do I need a certification to scuba dive in New Zealand?',
        a: 'For guided boat dives, operators generally require an Open Water certification or higher from a recognised agency (PADI, SSI, NAUI). Non-divers can join discover scuba experiences or snorkel trips at many sites.',
      },
      {
        q: 'What are the best dive sites in New Zealand?',
        a: 'The Poor Knights Islands marine reserve is rated among the world\'s best temperate dive sites. Other highlights include the Bay of Islands wrecks (Rainbow Warrior, HMNZS Canterbury), Fiordland\'s black coral, and the marine reserves around Goat Island and Taputeranga.',
      },
      {
        q: 'When is the best season for diving in NZ?',
        a: 'Summer and autumn (December to May) bring the warmest water, 16-22°C, and often the best visibility. Winter diving is excellent in places like the Poor Knights when large swells are absent, but a 7mm wetsuit or drysuit is essential.',
      },
      {
        q: 'Can I rent dive gear in New Zealand?',
        a: 'Yes, dive shops and charter operators rent full kits including wetsuits, BCDs, regulators, tanks and weights. Bring your certification card; you will need it to rent life-support equipment and fill tanks.',
      },
      {
        q: 'Are there age limits for diving and water sports?',
        a: 'Junior dive certifications start from age 10-12 with depth and supervision limits. For activities like wakeboarding, jet boating and parasailing, operators set their own age, weight and health requirements, so check when booking.',
      },
    ],
  },
  'fishing-charters': {
    slug: 'fishing-charters',
    description: 'New Zealand fishing charters target snapper, kingfish, hapuku, marlin and more. No fishing licence is needed for saltwater recreational fishing in NZ.',
    faqs: [
      {
        q: 'Do I need a fishing licence for sea fishing in NZ?',
        a: 'No licence is required for recreational saltwater fishing in New Zealand. However, MPI size and bag limits apply to all species, and some areas are closed or restricted. Charter skippers know the rules and will keep you legal.',
      },
      {
        q: 'What is provided on a fishing charter?',
        a: 'Most charters include rods, reels, bait, tackle and ice for your catch, plus the skipper\'s local knowledge. Bring warm clothing, sun protection, food and drink unless catering is listed, and a chilly bin to take your fish home.',
      },
      {
        q: 'How much does a fishing charter cost in NZ?',
        a: 'Shared (per-person) trips typically run $120-$250 for a half or full day. Private charters for a group start around $800 and can exceed $3,000 for overnight or game fishing trips.',
      },
      {
        q: 'What fish can I catch on a NZ charter?',
        a: 'Snapper and kingfish are the mainstays in northern waters year-round. Seasonal targets include hapuku, tarakihi, gurnard, trevally and john dory, with marlin and tuna running in summer. Southern charters target blue cod, groper and trumpeter.',
      },
      {
        q: 'Can I keep the fish I catch on a charter?',
        a: 'Yes, within legal size and bag limits. Most skippers will fillet your catch, and many practise catch-and-release for breeding-sized fish. Ask about the operator\'s policy when booking.',
      },
    ],
  },
  'fuel-bunkering': {
    slug: 'fuel-bunkering',
    description: 'Marine fuel suppliers and bunkering services across New Zealand, from marina fuel berths to bulk delivery for commercial vessels.',
    faqs: [
      {
        q: 'Where can I refuel my boat in New Zealand?',
        a: 'Most major marinas have fuel berths selling petrol and diesel, and some coastal towns have dedicated marine fuel wharves. Remote areas may require jerry cans or arranged delivery. Plan fuel stops on longer coastal passages.',
      },
      {
        q: 'Can I use regular car petrol in my boat?',
        a: 'Check your engine manual. Many modern outboards run fine on 91 octane, but ethanol-blended fuels (E10) can damage older outboard fuel systems and fibreglass tanks. Marine-grade or ethanol-free petrol is the safer choice where available.',
      },
      {
        q: 'What is bunkering?',
        a: 'Bunkering is the supply of fuel in bulk to larger vessels, delivered by truck or barge directly alongside. Commercial operators, superyachts and ships use bunkering services at ports like Auckland, Tauranga and Lyttelton.',
      },
      {
        q: 'Why is marine fuel more expensive than road fuel?',
        a: 'Marine fuel costs more due to lower volumes, waterside infrastructure costs and delivery logistics. Prices vary significantly between marinas, so it pays to compare, especially before a big trip.',
      },
      {
        q: 'How do I avoid fuel contamination problems?',
        a: 'Water in fuel and diesel bug are the main culprits. Keep tanks full to reduce condensation, use fuel stabiliser for petrol stored over winter, fit quality fuel-water separators, and have diesel polished if the boat sits unused for long periods.',
      },
    ],
  },
  'marinas-berths': {
    slug: 'marinas-berths',
    description: 'New Zealand marinas offer berths from 8m trailer boat pens to superyacht berths, plus hardstand, haulout and liveaboard facilities. Compare options across all regions.',
    faqs: [
      {
        q: 'How much does a marina berth cost in New Zealand?',
        a: 'Annual berth fees typically range from $3,000 for a small pen at a regional marina to $15,000+ for a large berth at premium Auckland marinas like Westhaven. Some marinas also sell berths outright or on long-term licence.',
      },
      {
        q: 'Are there waiting lists for marina berths?',
        a: 'Yes, popular marinas in Auckland, Tauranga and Whangarei have waiting lists, sometimes years long for larger berths. Join lists early, and consider regional marinas or pile moorings as an interim option.',
      },
      {
        q: 'Can I live aboard my boat in a NZ marina?',
        a: 'Most marinas restrict or prohibit living aboard, and those that allow it usually cap numbers and charge extra. Council rules also apply. Always get written permission from the marina before moving aboard.',
      },
      {
        q: 'What is the difference between a berth and a mooring?',
        a: 'A berth is a walk-on marina pen with power, water and security. A mooring (swing or pile) anchors the boat in open water, costing a fraction of berth fees but requiring dinghy access and offering no services.',
      },
      {
        q: 'How secure are New Zealand marinas?',
        a: 'Major marinas have gated access, CCTV and night security patrols, but security levels vary. Remove valuables and electronics when leaving the boat, lock hatches, and check your insurer\'s marina security requirements.',
      },
    ],
  },
  'marine-chandlers': {
    slug: 'marine-chandlers',
    description: 'Marine chandlers stock everything the NZ boater needs: rope, paint, safety gear, fittings, fasteners and electronics, with expert advice on tap.',
    faqs: [
      {
        q: 'What does a marine chandlery sell?',
        a: 'Chandleries stock rope and cordage, marine paint and antifouling, safety equipment, stainless fittings, fasteners, pumps, plumbing, electrical supplies, clothing and general boating hardware. Many also sell charts, books and fishing gear.',
      },
      {
        q: 'Can chandlers order in parts they do not stock?',
        a: 'Yes. Most chandlers can order parts from local distributors or overseas suppliers, often within days. Bring the part number, engine serial number or a photo of the old part to make identification faster.',
      },
      {
        q: 'Do chandleries offer trade accounts?',
        a: 'Yes, most offer trade accounts with discounted pricing for boat builders, repairers and marine businesses. Some also run loyalty programmes for regular retail customers.',
      },
      {
        q: 'What safety gear should I buy for a small boat?',
        a: 'Legally you need correctly sized lifejackets for everyone aboard. Beyond that, carry an anchor and rope, bailer, paddle, torch, VHF radio or waterproof phone case, flares or a distress beacon for coastal waters, and a first aid kit.',
      },
      {
        q: 'Is it safe to buy marine safety gear online?',
        a: 'Yes, but buy from reputable NZ suppliers and check products meet relevant standards, for example NZS 5823 or ISO 12402 for lifejackets. Counterfeit safety gear from overseas marketplaces is a real risk.',
      },
    ],
  },
  'marine-electronics': {
    slug: 'marine-electronics',
    description: 'Marine electronics specialists supply and install chartplotters, fishfinders, radar, VHF radios, autopilots and complete navigation systems for NZ vessels.',
    faqs: [
      {
        q: 'What electronics do I need on my boat?',
        a: 'The essentials are a VHF radio, a chartplotter or GPS, and a depth sounder. Offshore boats should add an EPIRB or PLB, radar and AIS. Fishfinders, autopilots and engine monitoring are popular upgrades.',
      },
      {
        q: 'Do I need a licence to use a marine VHF radio?',
        a: 'Yes. Operating a marine VHF in NZ requires a VHF Operator\'s Certificate, available through Coastguard Boating Education as a one-day course. The qualification covers correct radio procedure and distress calling.',
      },
      {
        q: 'Who should install marine electronics?',
        a: 'Use a specialist marine electronics installer, especially for networked systems using NMEA 2000. Poor DIY wiring is a leading cause of electronics faults and corrosion problems on boats.',
      },
      {
        q: 'How much does a chartplotter cost?',
        a: 'Entry-level chartplotter/fishfinder combos start around $500-$800, mid-range units run $1,500-$3,000, and large networked multi-function displays exceed $5,000, plus transducers, charts and installation.',
      },
      {
        q: 'What waterproof rating do marine electronics need?',
        a: 'Look for IPX6 or IPX7 ratings for anything exposed to spray or rain. IPX7 means the unit survives 30 minutes submerged at 1 metre. Open boats and jetskis need fully waterproof gear.',
      },
    ],
  },
  'marine-engineering': {
    slug: 'marine-engineering',
    description: 'Marine engineers keep NZ vessels running: engine servicing, repowers, propulsion systems, gensets, driveline work and emergency repairs.',
    faqs: [
      {
        q: 'What does a marine engineer do?',
        a: 'Marine engineers service and repair inboard engines, outboards, gensets, gearboxes, shafting, propellers and fuel and cooling systems. They also handle repowers, engine alignment, sea trials and pre-purchase mechanical inspections.',
      },
      {
        q: 'How often should a boat engine be serviced?',
        a: 'Follow the manufacturer schedule, typically annually or every 100-250 running hours, whichever comes first. Saltwater use demands strict cooling system maintenance, including impeller replacement every 1-2 years.',
      },
      {
        q: 'Petrol or diesel for a boat?',
        a: 'Petrol outboards dominate the trailer boat market for their light weight and low upfront cost. Diesel inboards suit larger vessels and long-range cruising, offering better fuel economy, longevity and lower fire risk.',
      },
      {
        q: 'Can marine engineers do insurance repair work?',
        a: 'Yes. For insurance claims, the insurer appoints an assessor who approves the repairer and scope of work. Choose an engineer experienced in insurance jobs; they will document damage and liaise with the assessor directly.',
      },
      {
        q: 'What is a heat exchanger service?',
        a: 'The heat exchanger cools a marine engine using seawater. Servicing involves removing, cleaning and pressure-testing the core, replacing seals and anodes, typically every 2-3 years. Neglect causes overheating and expensive engine damage.',
      },
    ],
  },
  'marine-insurance': {
    slug: 'marine-insurance',
    description: 'Marine insurance specialists cover everything from trailer boats to superyachts: agreed value policies, third party liability, racing cover and salvage.',
    faqs: [
      {
        q: 'Is boat insurance compulsory in New Zealand?',
        a: 'No, it is not legally required. However, most marinas require third party liability cover as a berth condition, and lenders require comprehensive cover on financed boats. Uninsured salvage and wreck removal costs can be enormous.',
      },
      {
        q: 'What does boat insurance cover?',
        a: 'Comprehensive policies cover accidental damage, theft, fire, storm damage and salvage, plus third party liability. Cover usually extends to trailers, tenders and equipment. Read exclusions carefully, especially around wear and tear and machinery breakdown.',
      },
      {
        q: 'How much does boat insurance cost in NZ?',
        a: 'Premiums typically run 1-2% of the vessel\'s value per year, depending on boat type, age, value, your experience and where it is kept. A $100,000 boat usually costs $1,000-$2,000 a year to insure.',
      },
      {
        q: 'What is the difference between agreed value and market value?',
        a: 'Agreed value policies pay a fixed sum set when the policy starts, with no depreciation arguments at claim time. Market value policies pay what the boat was worth just before the loss, which can be less than you expect.',
      },
      {
        q: 'Does boat insurance cover yacht racing?',
        a: 'Standard policies often exclude racing. Most insurers offer racing extensions for an additional premium, covering you for club racing through to offshore events. Declare your racing intentions before the season starts.',
      },
    ],
  },
  'marine-services': {
    slug: 'marine-services',
    description: 'Marine services keep boats looking good and running sweet: antifouling, detailing, valeting, hardstand facilities, boat management and more.',
    faqs: [
      {
        q: 'What falls under marine services?',
        a: 'Antifouling and hull cleaning, boat detailing and valeting, hardstand and haulout facilities, rigging checks, boat sitting and management, deliveries, and general maintenance programmes.',
      },
      {
        q: 'How much does antifouling cost in NZ?',
        a: 'Including haulout, waterblasting, preparation and paint, expect roughly $100-$150 per metre for a standard job. Extra coats, propspeed, anodes and repairs add to the total. Hardstand time is usually charged separately.',
      },
      {
        q: 'How often should a boat be antifouled?',
        a: 'Annually in most NZ waters, where warm temperatures drive aggressive marine growth. Boats in high-fouling areas or used infrequently may need attention sooner; quality ablative paints can stretch the interval.',
      },
      {
        q: 'What is a boat valet service?',
        a: 'A valet is a professional wash and detail: hull and topsides, decks, cockpit, interior, windows and stainless. Regular valeting protects gelcoat and canvas, and keeps the boat ready to use.',
      },
      {
        q: 'Are there environmental rules for boat maintenance?',
        a: 'Yes. Regional councils regulate washdown and sanding runoff, especially antifouling residues, which must be contained and disposed of properly. Use approved hardstand facilities with filtration, and check council rules before DIY work near the water.',
      },
    ],
  },
  'sailmakers-canvas': {
    slug: 'sailmakers-canvas',
    description: 'NZ sailmakers and canvas specialists design, build and repair sails, biminis, dodgers, covers and upholstery for yachts, launches and trailer boats.',
    faqs: [
      {
        q: 'How long do yacht sails last?',
        a: 'Cruising sails typically last 5-10 years depending on use, UV exposure and material. Racing sails have a shorter competitive life. Regular inspection, timely repairs and proper storage extend sail life significantly.',
      },
      {
        q: 'Can damaged sails be repaired?',
        a: 'Yes. Sailmakers repair tears, blown stitching, damaged corners and chafe. Older sails can be recut to restore shape. If the cloth itself is degraded, replacement is usually more economical than extensive repairs.',
      },
      {
        q: 'What canvas work do boats commonly need?',
        a: 'Biminis and dodgers for sun and spray protection, cockpit enclosures, boat covers, sail covers, hatch and window covers, and upholstered squabs. Good canvas transforms comfort aboard and protects the boat.',
      },
      {
        q: 'How much does a new mainsail cost?',
        a: 'For a typical 30-40ft cruising yacht, a new dacron mainsail runs roughly $3,000-$6,000, with laminate performance sails from $6,000-$10,000+. Furling systems, battens and hardware are additional.',
      },
      {
        q: 'What fabric is best for marine covers?',
        a: 'Solution-dyed acrylic canvas (like Sunbrella) is the premium choice for covers and biminis, offering excellent UV resistance and breathability. PVC is fully waterproof and cheaper but heavier and less breathable.',
      },
    ],
  },
  'training-courses': {
    slug: 'training-courses',
    description: 'Boating education in New Zealand, from Coastguard Day Skipper courses to VHF radio certificates, Boatmaster qualifications and jetski courses.',
    faqs: [
      {
        q: 'Do I need a licence to drive a boat in New Zealand?',
        a: 'No general licence is required for recreational boating in NZ. However, training is strongly recommended, and some local bylaws, rental operators and insurers require proof of competence. Maritime NZ sets the safety rules everyone must follow.',
      },
      {
        q: 'What is the Day Skipper course?',
        a: 'Day Skipper is Coastguard Boating Education\'s popular introductory certificate covering navigation, rules of the road, safety equipment, weather and basic boat handling. It takes about 15 hours and suits new boat owners.',
      },
      {
        q: 'How do I get a VHF radio certificate?',
        a: 'The VHF Operator\'s Certificate is a one-day course through Coastguard Boating Education covering radio procedure, channels, distress calling and legal requirements. It is required to legally operate a marine VHF.',
      },
      {
        q: 'What is the Boatmaster certificate?',
        a: 'Boatmaster is a comprehensive 30-hour course covering navigation, meteorology, seamanship, tides and collision rules in depth. It suits skippers of larger vessels and those heading further offshore.',
      },
      {
        q: 'Are there courses for jetskis and other watercraft?',
        a: 'Yes. Personal watercraft (PWC) courses cover jetski handling, rules and safety, and are required by some regional bylaws and rental operators. Kayak, waka and sailing courses are also widely available.',
      },
    ],
  },
};
